// Add Video guide — the AI-powered video modal (VideoModal: AI Generate → storyboard,
// library and upload tabs, plus the Activity Log toggle in the header).
// Locale-keyed like every guide (content.<lang> → { feature, title, sections }).

const addVideoGuide = {
  id: "add-video",
  icon: "videocam",
  keywords: [
    "add video",
    "video ekle",
    "ai video",
    "generate video",
    "video oluştur",
    "storyboard",
    "create storyboard",
    "storyboard oluştur",
    "generate all scenes",
    "tüm sahneleri oluştur",
    "starting image",
    "başlangıç görseli",
    "preview image",
    "önizleme görseli",
    "omni",
    "veo",
    "grok",
    "seedance",
    "ltx",
    "text in video",
    "videoda metin",
    "overlay",
    "bindirme",
    "upload video",
    "video yükle",
    "video library",
    "activity log",
    "etkinlik günlüğü",
    "credits",
    "kredi",
    "agregar video",
    "vídeo",
    "video con ia",
    "generar video",
    "crear storyboard",
    "generar todas las escenas",
    "imagen inicial",
    "texto en video",
    "subir video",
    "registro de actividad",
    "créditos",
  ],
  content: {
    en: {
      feature: "AI",
      title: "Add Video",
      sections: [
        {
          id: "overview",
          title: "Adding a video",
          videoUrl: null,
          text:
            "Click **Add Video** among the AI tools in the top bar to put a video on the current scene. The modal has three tabs: **AI Generate**, **Choose from library** and **Upload my video**. The clock icon in the modal header opens the **Activity Log** of your past video generations.\n\n" +
            "- AI video generation spends **credits**; your balance is shown next to your name in the top bar.\n" +
            "- AI generation works in two steps: **Create Storyboard** first splits your script into scenes, then you generate a video for each scene (see **The storyboard** below).\n" +
            "- Video jobs run in the background and also appear in the job tray, where a finished clip can be approved onto the scene it was made for.",
        },
        {
          id: "ai-generate",
          title: "AI Generate tab",
          videoUrl: null,
          text:
            "Fill in the form, then click **Create Storyboard** in the footer.\n\n" +
            "- **Write Prompt** — describe each scene in its own tab (**Scene 1**, **Scene 2**…), adding scenes with **+**. Each written scene becomes an 8-second clip.\n" +
            "- **From Narration** — loads the current scene's narration. Each sentence becomes one storyboard scene, so remove the sentences that don't need visuals.\n" +
            "- **AI Provider** — **Auto** uses Omni. **Custom** lets you pick Omni, Grok, Seedance or LTX; only the aspect ratios the chosen model supports are offered.\n" +
            "- **Duration** — **Auto**; in **From Narration** mode you can also choose **Custom** and enter a length in seconds.\n" +
            "- **Style** — pick a style card, upload a **Style Reference** image to copy its look, or choose **Custom** and describe your own style.\n" +
            "- **Optional Enhancements** — set the **Aspect Ratio**. Under **Advanced Settings**, **Text in Video** adds text and image overlays with their own position and styling, shown in a live preview.",
        },
        {
          id: "upload",
          title: "Uploading a video",
          videoUrl: null,
          text:
            "Prefer your own footage? Switch to the **Upload my video** tab.\n\n" +
            "- Drag and drop a video onto the drop zone, or click to browse — MP4, MOV, WEBM and other common video formats, up to **500 MB**.\n" +
            "- Click **Upload file**; the video is added to the current scene as soon as the upload finishes. **Clear** lets you pick a different file instead.",
        },
        {
          id: "storyboard",
          title: "The storyboard",
          videoUrl: null,
          text:
            "After **Create Storyboard**, the modal switches to the storyboard: one card per scene, each with its own video prompt.\n\n" +
            "- Scenes are generated in order. Click **Create the Scene** on the next waiting card, or **Generate All Scenes** in the footer to queue every remaining scene. The footer's **Credits** button estimates the cost of the next scene before you continue.\n" +
            "- **Edit** changes a scene's video prompt; **Generate Preview Image** makes a still so you can check the look first.\n" +
            "- On the first scene you can set a **Starting image** — upload one, or pick an image from the scene or a preview image with **Select as reference** — and the video starts from it.\n" +
            "- A finished card offers **Add to Scene**, **Download** and **Regenerate**; a scene that is still generating can be stopped with **Cancel**.\n" +
            "- **Add Scene** inserts a new scene at the end or after a scene you choose.\n" +
            "- **Back to Form** keeps the storyboard: the AI tab then shows **Ongoing Storyboard** with a **View** button so you can continue later. **Clear** discards it, and starting a new storyboard while one exists asks for confirmation first.",
        },
        {
          id: "library-history",
          title: "Library and Activity Log",
          videoUrl: null,
          text:
            "- **Choose from library** — browse or search the videos in your library; double-click a video, or use its **+** button, to add it to the scene. If you have none yet, **Upload now** takes you to the upload tab.\n" +
            "- **Activity Log** — the clock icon lists this training's recent AI video generations with your **Current Balance** and **Video Spending**; **All Activities** shows every kind of AI job. Double-click a row, or use its **+** button, to add a past clip to the current scene without spending credits again.",
        },
      ],
    },
    tr: {
      feature: "Yapay Zeka",
      title: "Video Ekle",
      sections: [
        {
          id: "overview",
          title: "Video ekleme",
          videoUrl: null,
          text:
            "Mevcut sahneye video eklemek için üst çubuktaki yapay zeka araçlarından **Video Ekle**'ye tıklayın. Modalda üç sekme vardır: **AI İle Oluştur**, **Kütüphaneden Seç** ve **Video Yükle**. Modal başlığındaki saat simgesi, geçmiş video üretimlerinizin yer aldığı **Etkinlik Günlüğü**'nü açar.\n\n" +
            "- Yapay zeka ile video oluşturmak **kredi** harcar; bakiyeniz üst çubukta adınızın yanında görünür.\n" +
            "- Yapay zeka ile üretim iki adımda ilerler: önce **Storyboard Oluştur** metninizi sahnelere böler, ardından her sahnenin videosunu üretirsiniz (aşağıdaki **Storyboard** bölümüne bakın).\n" +
            "- Video işleri arka planda çalışır ve iş kuyruğunda (widget) da görünür; tamamlanan bir klip oradan onaylanarak üretildiği sahneye eklenebilir.",
        },
        {
          id: "ai-generate",
          title: "AI İle Oluştur sekmesi",
          videoUrl: null,
          text:
            "Formu doldurun, ardından alt kısımdaki **Storyboard Oluştur**'a tıklayın.\n\n" +
            "- **Prompt Yaz** — her sahneyi kendi sekmesinde (**Sahne 1**, **Sahne 2**…) tanımlayın; yeni sahneleri **+** ile ekleyin. Yazdığınız her sahne 8 saniyelik bir klip olur.\n" +
            "- **Anlatımdan** — mevcut sahnenin anlatım metnini getirir. Her cümle bir storyboard sahnesine dönüşür; görsel desteğe ihtiyaç duymayan cümleleri silin.\n" +
            "- **AI Sağlayıcısı** — **Otomatik**, Omni'yi kullanır. **Özel** ile Omni, Grok, Seedance veya LTX'i seçebilirsiniz; yalnızca seçilen modelin desteklediği en boy oranları sunulur.\n" +
            "- **Süre** — **Otomatik**; **Anlatımdan** modunda **Özel**'i seçip süreyi saniye olarak da girebilirsiniz.\n" +
            "- **Stil** — bir stil kartı seçin, görünümünü örnek almak için bir **Stil Referansı** görseli yükleyin ya da **Özel**'i seçip kendi stilinizi tanımlayın.\n" +
            "- **Opsiyonel İyileştirmeler** — **En Boy Oranı**'nı ayarlayın. **Gelişmiş Ayarlar** altındaki **Videoda Metin**, konumu ve stili ayarlanabilen metin ve görsel bindirmeleri ekler; sonuç canlı önizlemede görünür.",
        },
        {
          id: "upload",
          title: "Video yükleme",
          videoUrl: null,
          text:
            "Kendi görüntülerinizi mi kullanmak istiyorsunuz? **Video Yükle** sekmesine geçin.\n\n" +
            "- Videoyu yükleme alanına sürükleyip bırakın veya tıklayıp seçin — MP4, MOV, WEBM ve diğer yaygın video biçimleri, en fazla **500 MB**.\n" +
            "- **Yükle**'ye tıklayın; yükleme biter bitmez video mevcut sahneye eklenir. Bunun yerine başka bir dosya seçmek için **Temizle**'yi kullanın.",
        },
        {
          id: "storyboard",
          title: "Storyboard",
          videoUrl: null,
          text:
            "**Storyboard Oluştur**'dan sonra modal storyboard görünümüne geçer: her sahne için kendi video prompt'unu içeren bir kart.\n\n" +
            "- Sahneler sırayla üretilir. Sıradaki bekleyen karttaki **Sahneyi Üret**'e ya da kalan tüm sahneleri sıraya almak için alt kısımdaki **Tüm Sahneleri Oluştur**'a tıklayın. Alt kısımdaki **Kredi** düğmesi, devam etmeden önce sıradaki sahnenin maliyetini tahmin eder.\n" +
            "- **Düzenle** sahnenin video prompt'unu değiştirir; **Önizleme Görseli Üret**, görünümü önceden kontrol edebilmeniz için bir kare üretir.\n" +
            "- İlk sahnede bir **Başlangıç görseli** belirleyebilirsiniz — bir görsel yükleyin ya da sahnedeki görsellerden veya önizleme görsellerinden birini **Referans olarak seç** ile seçin; video bu görselden başlar.\n" +
            "- Tamamlanan kartta **Sahneye Ekle**, **İndir** ve **Yeniden Oluştur** bulunur; üretimi süren bir sahne **İptal** ile durdurulabilir.\n" +
            "- **Sahne Ekle**, yeni bir sahneyi sona ya da seçtiğiniz bir sahnenin arkasına ekler.\n" +
            "- **Geri Dön** storyboard'u korur: AI sekmesinde daha sonra devam edebilmeniz için **Görüntüle** düğmesiyle birlikte **Devam Eden Storyboard** görünür. **Temizle** storyboard'u siler; mevcut bir storyboard varken yenisini başlatmak önce onay ister.",
        },
        {
          id: "library-history",
          title: "Kütüphane ve Etkinlik Günlüğü",
          videoUrl: null,
          text:
            "- **Kütüphaneden Seç** — kütüphanenizdeki videolara göz atın veya arama yapın; sahneye eklemek için videoya çift tıklayın ya da **+** düğmesini kullanın. Henüz videonuz yoksa **Hemen yükle** sizi yükleme sekmesine götürür.\n" +
            "- **Etkinlik Günlüğü** — saat simgesi, bu eğitimdeki son yapay zeka video üretimlerini **Mevcut Bakiye** ve **Video Harcaması** ile birlikte listeler; **Tüm Aktiviteler** tüm yapay zeka iş türlerini gösterir. Geçmiş bir klibi yeniden kredi harcamadan mevcut sahneye eklemek için satıra çift tıklayın veya **+** düğmesini kullanın.",
        },
      ],
    },
    es: {
      feature: "IA",
      title: "Agregar vídeo",
      sections: [
        {
          id: "overview",
          title: "Añadir un vídeo",
          videoUrl: null,
          text:
            "Haz clic en **Agregar Video** entre las herramientas de IA de la barra superior para poner un vídeo en la escena actual. La ventana tiene tres pestañas: **Generar con IA**, **Elegir de biblioteca** y **Subir mi video**. El icono del reloj en el encabezado de la ventana abre el **Registro de Actividad** con tus generaciones de vídeo anteriores.\n\n" +
            "- La generación de vídeo con IA consume **créditos**; tu saldo aparece junto a tu nombre en la barra superior.\n" +
            "- La generación con IA funciona en dos pasos: primero **Crear Storyboard** divide tu guion en escenas y, después, generas un vídeo para cada escena (consulta **El storyboard** más abajo).\n" +
            "- Los trabajos de vídeo se ejecutan en segundo plano y también aparecen en la bandeja de trabajos, donde un clip terminado puede aprobarse para añadirlo a la escena para la que se creó.",
        },
        {
          id: "ai-generate",
          title: "Pestaña Generar con IA",
          videoUrl: null,
          text:
            "Rellena el formulario y haz clic en **Crear Storyboard** en el pie de la ventana.\n\n" +
            "- **Escribir Prompt**: describe cada escena en su propia pestaña (**Escena 1**, **Escena 2**…) y añade escenas con **+**. Cada escena escrita se convierte en un clip de 8 segundos.\n" +
            "- **Desde Narración**: carga la narración de la escena actual. Cada frase se convierte en una escena del storyboard, así que elimina las frases que no necesiten imágenes.\n" +
            "- **Proveedor de IA**: **Automático** usa Omni. **Personalizado** te permite elegir Omni, Grok, Seedance o LTX; solo se ofrecen las relaciones de aspecto que admite el modelo elegido.\n" +
            "- **Duración**: **Auto**; en el modo **Desde Narración** también puedes elegir **Personalizado** e indicar una duración en segundos.\n" +
            "- **Estilo**: elige una tarjeta de estilo, sube una imagen de **Referencia de Estilo** para copiar su aspecto o elige **Personalizado** y describe tu propio estilo.\n" +
            "- **Mejoras Opcionales**: define la **Relación de Aspecto**. En **Configuración Avanzada**, **Texto en Video** añade superposiciones de texto e imagen con su propia posición y estilo, que se muestran en una vista previa en directo.",
        },
        {
          id: "upload",
          title: "Subir un vídeo",
          videoUrl: null,
          text:
            "¿Prefieres usar tus propias grabaciones? Cambia a la pestaña **Subir mi video**.\n\n" +
            "- Arrastra y suelta un vídeo en la zona de carga o haz clic para buscarlo: MP4, MOV, WEBM y otros formatos de vídeo habituales, de hasta **500 MB**.\n" +
            "- Haz clic en **Subir Fichero**; el vídeo se añade a la escena actual en cuanto termina la subida. **Borrar** te permite elegir otro archivo en su lugar.",
        },
        {
          id: "storyboard",
          title: "El storyboard",
          videoUrl: null,
          text:
            "Después de **Crear Storyboard**, la ventana pasa al storyboard: una tarjeta por escena, cada una con su propio prompt de vídeo.\n\n" +
            "- Las escenas se generan en orden. Haz clic en **Crear la Escena** en la siguiente tarjeta pendiente, o en **Generar Todas las Escenas** en el pie para poner en cola todas las escenas restantes. El botón **Créditos** del pie estima el coste de la siguiente escena antes de continuar.\n" +
            "- **Editar** cambia el prompt de vídeo de una escena; **Generar Imagen de Vista Previa** crea una imagen fija para que compruebes antes el aspecto.\n" +
            "- En la primera escena puedes definir una **Imagen inicial** (sube una, o elige una imagen de la escena o una imagen de vista previa con **Seleccionar como referencia**) y el vídeo empezará a partir de ella.\n" +
            "- Una tarjeta terminada ofrece **Agregar a Escena**, **Descargar** y **Regenerar**; una escena que todavía se está generando puede detenerse con **Cancelar**.\n" +
            "- **Añadir Escena** inserta una escena nueva al final o después de la escena que elijas.\n" +
            "- **Volver al Formulario** conserva el storyboard: la pestaña de IA muestra entonces **Storyboard en Curso** con un botón **Ver** para que puedas continuar más tarde. **Limpiar** lo descarta, y empezar un storyboard nuevo mientras ya existe uno pide confirmación primero.",
        },
        {
          id: "library-history",
          title: "Biblioteca y Registro de Actividad",
          videoUrl: null,
          text:
            "- **Elegir de biblioteca**: explora o busca los vídeos de tu biblioteca; haz doble clic en un vídeo, o usa su botón **+**, para añadirlo a la escena. Si todavía no tienes ninguno, **Subir ahora** te lleva a la pestaña de subida.\n" +
            "- **Registro de Actividad**: el icono del reloj muestra las generaciones de vídeo con IA recientes de esta formación junto con tu **Saldo Actual** y el **Gasto en Videos**; **Todas las Actividades** muestra todos los tipos de trabajos de IA. Haz doble clic en una fila, o usa su botón **+**, para añadir un clip anterior a la escena actual sin volver a gastar créditos.",
        },
      ],
    },
  },
};

export default addVideoGuide;
